"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_speech_recognition_1 = __importDefault(require("react-speech-recognition"));
const ONE_SECOND = 1000;
const VOICE_VOLUME_GRANULARITY = 0.1; //dB
const VOLUME_DETECTION_FREQUENCY = 30; //Hz
const calculateAudioVolume = (audioTimeData) => (Math.pow(Math.max(...audioTimeData), 2));
const audioInputSource = {
    stream: null,
};
const volumeTrackerRef = {
    value: null,
};
const nativeHandlers = {
    speechVolumeChange: null,
};
const audioContext = new window.AudioContext();
const analyser = audioContext.createAnalyser();
const speechData = new Float32Array(analyser.frequencyBinCount);
const speechRecognition = react_speech_recognition_1.default.getRecognition();
if (speechRecognition) {
    speechRecognition.interimResults = true;
}
const Voice = {
    _loaded: false,
    _listeners: null,
    _events: {
        onSpeechStart: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechRecognized: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechEnd: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechError: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechResults: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechPartialResults: function (e) {
            throw new Error('Function not implemented.');
        },
        onSpeechVolumeChanged: function (e) {
            throw new Error('Function not implemented.');
        }
    },
    removeAllListeners: function () {
        this.onSpeechStart = function (e) { };
        this.onSpeechEnd = function (e) { };
        this.onSpeechError = function (e) { };
        this.onSpeechResults = function (e) { };
        this.onSpeechPartialResults = function (e) { };
        this.onSpeechVolumeChanged = function (e) { };
        if (speechRecognition) {
            speechRecognition.removeEventListener('speechstart', webOnSpeechStartHandler);
            speechRecognition.removeEventListener('speechend', webOnSpeechEndHandler);
            speechRecognition.removeEventListener('error', webOnSpeechErrorHandler);
            speechRecognition.removeEventListener('result', webOnResultHandler);
            speechRecognition.removeEventListener('result', webOnPartialResultHandler);
        }
    },
    destroy: function () {
        throw new Error('Function not implemented.');
    },
    start: async function (locale, options) {
        this._loaded = true;
        try {
            if (nativeHandlers.speechVolumeChange) {
                await window.navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
                    audioInputSource.stream = audioContext.createMediaStreamSource(stream);
                    audioInputSource.stream.connect(analyser);
                });
                startVolumeTracker();
            }
            return await react_speech_recognition_1.default.startListening({ language: locale, ...options });
        }
        catch (error) {
            this._loaded = false;
            throw error;
        }
    },
    stop: async function () {
        audioInputSource.stream?.mediaStream.getTracks().forEach((track) => track.stop());
        if (this._loaded) {
            if (volumeTrackerRef.value !== null) {
                cancelAnimationFrame(volumeTrackerRef.value);
                volumeTrackerRef.value = null;
            }
            return react_speech_recognition_1.default.stopListening();
        }
    },
    cancel: function () {
        throw new Error('Function not implemented.');
    },
    isAvailable: function () {
        throw new Error('Function not implemented.');
    },
    getSpeechRecognitionServices: function () {
        throw new Error('Function not implemented.');
    },
    isRecognizing: function () {
        throw new Error('Function not implemented.');
    },
    onSpeechStart: function (e) { },
    onSpeechRecognized: function (e) {
        throw new Error('Function not implemented.');
    },
    onSpeechEnd: function (e) { },
    onSpeechError: function (e) { },
    onSpeechResults: function (e) { },
    onSpeechPartialResults: function (e) { },
    onSpeechVolumeChanged: function (e) { },
};
const startVolumeTracker = () => {
    const volumeDetection = {
        currentValue: 0,
        nextMeasureTime: 0,
    };
    const trackVolume = (trackTime) => {
        if (trackTime > volumeDetection.nextMeasureTime) {
            volumeDetection.nextMeasureTime = trackTime + ONE_SECOND / VOLUME_DETECTION_FREQUENCY;
            analyser.getFloatTimeDomainData(speechData);
            const inputVolume = calculateAudioVolume(speechData);
            if (Math.abs(inputVolume - volumeDetection.currentValue) > VOICE_VOLUME_GRANULARITY) {
                volumeDetection.currentValue = inputVolume;
                Voice.onSpeechVolumeChanged({
                    value: inputVolume,
                });
            }
        }
        volumeTrackerRef.value = requestAnimationFrame(trackVolume);
    };
    requestAnimationFrame(trackVolume);
};
const webOnResultHandler = ({ resultIndex, results }) => {
    if (results[resultIndex].isFinal) {
        Voice.onSpeechResults({
            value: (Array.from(results)
                .flatMap((speechResultList) => Array.from(speechResultList))
                .map((speechResult) => speechResult.transcript))
        });
    }
};
const webOnPartialResultHandler = ({ resultIndex, results }) => {
    if (!results[resultIndex].isFinal) {
        Voice.onSpeechPartialResults({
            value: (Array.from(results)
                .flatMap((speechResultList) => [...speechResultList])
                .map((speechResult) => speechResult.transcript))
        });
    }
};
const webOnSpeechStartHandler = () => {
    Voice.onSpeechStart({});
};
const webOnSpeechEndHandler = () => {
    Voice.onSpeechEnd({});
};
const webOnSpeechErrorHandler = ({ error, message }) => {
    Voice.onSpeechError({ error: {
            code: error,
            message,
        } });
};
const VoiceProxy = new Proxy(Voice, {
    get(...args) {
        const value = Reflect.get(...args);
        return typeof value == 'function' ? value.bind(args[0]) : value;
    },
    set(...args) {
        if (speechRecognition) {
            const voiceAPIKey = args[1];
            const voiceAPIValue = args[2];
            switch (voiceAPIKey) {
                case 'onSpeechStart':
                    speechRecognition.removeEventListener('speechstart', webOnSpeechStartHandler);
                    speechRecognition.addEventListener('speechstart', webOnSpeechStartHandler);
                    break;
                case 'onSpeechEnd':
                    speechRecognition.removeEventListener('speechend', webOnSpeechEndHandler);
                    speechRecognition.addEventListener('speechend', webOnSpeechEndHandler);
                    break;
                case 'onSpeechError':
                    speechRecognition.removeEventListener('error', webOnSpeechErrorHandler);
                    speechRecognition.addEventListener('error', webOnSpeechErrorHandler);
                    break;
                case 'onSpeechResults':
                    speechRecognition.removeEventListener('result', webOnResultHandler);
                    speechRecognition.addEventListener('result', webOnResultHandler);
                    break;
                case 'onSpeechPartialResults':
                    speechRecognition.removeEventListener('result', webOnPartialResultHandler);
                    speechRecognition.addEventListener('result', webOnPartialResultHandler);
                    break;
                case 'onSpeechVolumeChanged':
                    nativeHandlers.speechVolumeChange = voiceAPIValue;
                    break;
                default: return false;
            }
        }
        return Reflect.set(...args);
    },
});
exports.default = VoiceProxy;
